/**
 * Developers / MCP hub — the canonical agent-and-developer home.
 *
 * InboxGuard is MCP-first: an AI agent can both *detect* a domain's email-DNS
 * posture and *remediate* it through the scan → get_dns_fix_plan → apply_dns_fix
 * loop. This page is the front door for that: connect the MCP server, drop in a
 * client config (Claude Desktop / ChatGPT / Cursor / Claude Code / any client),
 * see the 28 tools, understand the fix loop, and find the REST API + SDKs +
 * discovery surface.
 *
 * Heavy MCP/agent content used to live inside /api-docs; it was crammed. This
 * page owns it now; /api-docs links here for agent builders and keeps a focused
 * REST reference.
 *
 * Honesty rules (keep in sync with /agents.md, /llms.txt, /openapi.json):
 *   - 28 MCP tools, exact names below. Discovery is open; tools/call needs a
 *     bearer API key.
 *   - `inboxguard` is LIVE on npm (npm install inboxguard); a Python SDK is still
 *     pending. Skills install via `npx skills add movaMedia-Inc/inboxguard-skills`.
 *   - apply_dns_fix is destructive, two-step, server-revalidated.
 */
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Bot,
  Plug,
  Wrench,
  Terminal,
  KeyRound,
  Check,
  Copy,
  ExternalLink,
  ArrowRight,
  ArrowDown,
  Search,
  ListChecks,
  ShieldCheck,
  Code as CodeIcon,
  BookOpen,
  Sparkles,
  Boxes,
} from 'lucide-react';
import { PublicHeader } from '@/components/PublicHeader';
import { PublicFooter } from '@/components/PublicFooter';
import { cn } from '@/lib/utils';

// Canonical MCP endpoint is the vanity subdomain. The bare host
// (https://mcp.inboxguard.io) serves the MCP server too, so a client that drops
// the /mcp path still works; api.inboxguard.io/mcp remains a working alias.
const MCP_URL = 'https://mcp.inboxguard.io/mcp';
const API_BASE = 'https://api.inboxguard.io';

/* ------------------------------------------------------------------ *
 * The 28 MCP tools — names + one-liners mirror /.well-known/mcp/
 * manifest.json. Discovery (initialize / tools/list) is open; tools/call
 * needs a bearer API key. Keep this list and the manifest identical.
 * ------------------------------------------------------------------ */
const TOOLS: Array<{ name: string; auth: 'open' | 'read' | 'write'; desc: string }> = [
  { name: 'scan_domain', auth: 'open', desc: 'Run a full deliverability scan — SPF, DKIM, DMARC, MTA-STS, TLS-RPT, MX TLS, BIMI, and DNS blocklists — and return a 0–100 score with per-check findings.' },
  { name: 'get_deliverability_score', auth: 'open', desc: 'Return the overall score and letter grade for a domain (runs a fresh scan under the hood).' },
  { name: 'check_blocklists', auth: 'open', desc: 'Check whether a domain or its MX-host IPs appear on supported DNS blocklists — queried authoritative-side, so no public-resolver false positives.' },
  { name: 'get_dmarc_summary', auth: 'read', desc: 'Summarize ingested DMARC aggregate (RUA) reports for a tracked domain: volume, pass rate, and the senders behind each source IP.' },
  { name: 'list_domains', auth: 'read', desc: "List the account's tracked domains with latest score, last scan time, and open alert count." },
  { name: 'get_domain', auth: 'read', desc: 'Full detail for one tracked domain: latest scan with per-check findings, score history, and alerts.' },
  { name: 'list_scans', auth: 'read', desc: 'List recent scans (id, domain, run time, score) across all tracked domains, or for one domain.' },
  { name: 'list_alerts', auth: 'read', desc: 'List deliverability alerts — score drops, check failures, and blocklist listings.' },
  { name: 'resolve_alert', auth: 'write', desc: 'Mark an alert resolved, or reopen it. Requires a write/full-scope API key.' },
  { name: 'get_dns_fix_plan', auth: 'read', desc: 'Preview the exact DNS-record changes that would fix a domain — the diff, computed against the live records.' },
  { name: 'apply_dns_fix', auth: 'write', desc: 'Publish a fix plan at the connected registrar. Destructive, two-step, and server-revalidated. Requires a write/full-scope key.' },
  { name: 'analyze_headers', auth: 'open', desc: 'Re-verify SPF/DKIM/DMARC/ARC from a raw email and compare InboxGuard’s verdict to the sender’s own Authentication-Results — spot forged or mismatched results. No account needed.' },
  { name: 'scan_domains_batch', auth: 'write', desc: 'Queue an async batch scan of up to 50 domains; returns a jobId immediately (dodges the 30s per-call limit).' },
  { name: 'get_scan_job', auth: 'read', desc: 'Poll a batch scan (from scan_domains_batch): status, progress, and per-domain scores as they finish.' },
  { name: 'remove_domain', auth: 'write', desc: 'Stop monitoring a domain and delete it (and its history). Destructive; owner/admin write/full key. (To add one, scan_domain with a key auto-tracks it.)' },
  { name: 'list_registrar_connections', auth: 'read', desc: 'List connected registrars (Cloudflare/Route 53/GoDaddy/Namecheap) + supported providers — check whether the fix loop is available.' },
  { name: 'create_notification_channel', auth: 'write', desc: 'Wire up alert delivery: webhook (HMAC-signed), Slack, Teams, PagerDuty, SMS, or email. Returns the channel id (+ signing_secret for webhooks).' },
  { name: 'create_share_link', auth: 'write', desc: 'Mint a read-only public report link (inboxguard.io/r/<token>) for a tracked domain. Owner/admin write/full key on a Pro plan.' },
  { name: 'connect_snds', auth: 'write', desc: "Store the org's Microsoft SNDS access key so InboxGuard syncs per-IP Outlook/Hotmail reputation daily. Owner/admin write/full key." },
  { name: 'get_snds_status', auth: 'read', desc: 'Whether Microsoft SNDS is connected, last sync time + status, tracked-IP count, and blocked-IP count.' },
  { name: 'get_snds_ip_stats', auth: 'read', desc: 'Latest per-IP SNDS reputation: filter result (GREEN/YELLOW/RED), complaint-rate band, spam-trap hits, message volume, and block status.' },
  { name: 'connect_inbox_placement', auth: 'write', desc: "Store the org's seed-list inbox-placement provider + key (MailReach or GlockApps; GlockApps needs a projectId). Owner/admin write/full key." },
  { name: 'get_inbox_placement_status', auth: 'read', desc: 'Whether an inbox-placement provider is connected, which one, and the test count; lists supported providers when not connected.' },
  { name: 'start_inbox_placement_test', auth: 'write', desc: 'Start a seed-list test — returns a testId, the seed addresses to mail, and an optional header to insert. Owner/admin write/full key.' },
  { name: 'get_inbox_placement_test', auth: 'read', desc: 'Poll a seed-list test by testId: status plus Inbox/Spam/Missing counts and a 0–100 placement score.' },
  { name: 'list_inbox_placement_tests', auth: 'read', desc: 'List recent inbox-placement tests for the org with their status and Inbox/Spam/Missing scores.' },
  { name: 'get_deliverability_report', auth: 'read', desc: "Structured deliverability report for a tracked domain — latest score + letter grade, each check's pass/warn/fail status, top issues, blocklist count, and DMARC policy, plus a pdfUrl for the branded one-page PDF." },
  { name: 'get_portfolio', auth: 'read', desc: 'Org-wide deliverability rollup across every monitored domain — average score + overall grade, grade distribution (A/B/C/D/F/unscored), total open alerts, the domains needing attention, and a per-client-group breakdown.' },
];

export function DevelopersPage() {
  return (
    <>
      <Helmet>
        <title>Developers &amp; AI agents — InboxGuard MCP server, API &amp; SDKs</title>
        <meta
          name="description"
          content="Built for AI agents. Connect InboxGuard's MCP server (https://mcp.inboxguard.io/mcp) to Claude, ChatGPT, Cursor, or any MCP client. 28 tools that detect AND fix email-DNS problems, a typed REST API, and TypeScript + Python SDKs."
        />
        <link rel="canonical" href="https://inboxguard.io/developers" />
        <meta property="og:title" content="Developers & AI agents — InboxGuard" />
        <meta
          property="og:description"
          content="MCP-first email deliverability. Connect the MCP server, scan and remediate a domain's DNS from any agent, and drop in copy-paste configs for Claude Desktop, ChatGPT, Cursor, and Claude Code."
        />
        <meta property="og:url" content="https://inboxguard.io/developers" />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index,follow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <PublicHeader />

        {/* Hero */}
        <section className="hero-grad relative overflow-hidden">
          <div className="container mx-auto max-w-5xl px-4 pb-14 pt-16 sm:pt-20">
            <div className="mx-auto max-w-3xl text-center animate-fade-in-up">
              <span className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
                <Bot className="h-3 w-3" />
                MCP-first · agent-native
              </span>
              <h1 className="text-balance text-4xl font-bold tracking-tight sm:text-5xl">
                Built for AI agents.
                <span className="bg-gradient-to-r from-primary to-emerald-400 bg-clip-text text-transparent">
                  {' '}Detect and fix, not just report.
                </span>
              </h1>
              <p className="mx-auto mt-5 max-w-2xl text-balance text-lg text-muted-foreground">
                InboxGuard ships a production MCP server with 28 tools. An agent can scan a
                domain&apos;s SPF, DKIM, DMARC, MTA-STS, BIMI, and blocklist posture, then
                publish the exact DNS fix at your registrar. No human pasting TXT records at
                midnight.
              </p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <a
                  href="#connect"
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  <Plug className="h-4 w-4" />
                  Connect the MCP server
                </a>
                <a
                  href="#install"
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-input bg-background px-6 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
                >
                  Install guides
                </a>
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Check className="h-3 w-3 text-success" />
                  Streamable HTTP
                </span>
                <span className="opacity-50">·</span>
                <span>Open discovery</span>
                <span className="opacity-50">·</span>
                <span>Bearer-auth tool calls</span>
                <span className="opacity-50">·</span>
                <span>REST + OpenAPI 3.1</span>
              </div>
            </div>
          </div>
        </section>

        <main className="container mx-auto max-w-5xl px-4 pb-20 sm:pb-24">
          {/* min-w-0 lets the long code lines scroll inside the column instead
              of blowing out the grid track on mobile (same fix as /api-docs). */}
          <div className="grid min-w-0 gap-8 lg:grid-cols-[210px_1fr]">
            <nav className="hidden lg:block">
              <div className="sticky top-24 rounded-lg border bg-card p-4 text-sm">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  On this page
                </p>
                <ul className="space-y-1.5">
                  <li><a className="font-medium text-primary hover:underline" href="#connect">Connect the MCP server</a></li>
                  <li><a className="text-muted-foreground hover:text-foreground" href="#install">Install guides</a></li>
                  <li><a className="text-muted-foreground hover:text-foreground" href="#tools">The 28 tools</a></li>
                  <li><a className="text-muted-foreground hover:text-foreground" href="#fix-loop">The fix loop</a></li>
                  <li><a className="text-muted-foreground hover:text-foreground" href="#api">REST API &amp; SDKs</a></li>
                  <li><a className="text-muted-foreground hover:text-foreground" href="#discovery">Discovery surface</a></li>
                </ul>
              </div>
            </nav>

            <div className="min-w-0 space-y-16 [&_code]:break-words">
              {/* ---------------------------------------------------------- *
                  CONNECT
               * ---------------------------------------------------------- */}
              <Section id="connect" icon={<Plug className="h-5 w-5" />} title="Connect the MCP server">
                <p>
                  InboxGuard runs a hosted{' '}
                  <a
                    href="https://modelcontextprotocol.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary hover:underline"
                  >
                    Model Context Protocol
                  </a>{' '}
                  server over Streamable HTTP. Point any MCP-compatible client at the URL below.
                  The bare host (<code>https://mcp.inboxguard.io</code>) serves it too, so the{' '}
                  <code>/mcp</code> path is optional. Discovery (<code>initialize</code> /{' '}
                  <code>tools/list</code>) is open, so a client can introspect the toolset with no
                  credentials. <code>tools/call</code> requires a bearer API key.
                </p>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <CopyField
                    label="MCP server (Streamable HTTP)"
                    value={MCP_URL}
                    mono
                  />
                  <CopyField
                    label="Bearer token (Authorization header)"
                    value="Authorization: Bearer ig_live_…"
                    mono
                  />
                </div>

                <Callout
                  tone="info"
                  icon={<KeyRound className="h-4 w-4" />}
                  title="Get an API key"
                >
                  Create a key in{' '}
                  <Link to="/settings#api-keys" className="font-medium text-primary hover:underline">
                    Settings → API keys
                  </Link>
                  . <code>read</code>-scope keys cover every GET-style tool; the two write tools
                  (<code>resolve_alert</code>, <code>apply_dns_fix</code>) need a{' '}
                  <code>write</code>/<code>full</code> key. You only see the secret once.
                </Callout>
              </Section>

              {/* ---------------------------------------------------------- *
                  INSTALL GUIDES
               * ---------------------------------------------------------- */}
              <Section id="install" icon={<Terminal className="h-5 w-5" />} title="Install guides">
                <p>
                  Real, copy-pasteable config for the clients people actually use. Swap{' '}
                  <code>ig_live_…</code> for your key. Every client talks to the same Streamable
                  HTTP endpoint, <code className="break-all">{MCP_URL}</code>.
                </p>
                <InstallGuides />
              </Section>

              {/* ---------------------------------------------------------- *
                  TOOLS
               * ---------------------------------------------------------- */}
              <Section id="tools" icon={<Wrench className="h-5 w-5" />} title="The 28 tools">
                <p>
                  Scan, monitor, and fix. Discovery (<code>tools/list</code>) is open, but every{' '}
                  <code>tools/call</code> needs a bearer key — anything marked <AuthPill auth="read" /> or{' '}
                  <AuthPill auth="write" /> needs a key of that scope. For a keyless one-off scan, use the
                  REST endpoint <code>POST /scan-domain</code> (5 / hour per IP).
                </p>
                <div className="overflow-hidden rounded-lg border bg-card">
                  {TOOLS.map((t, i) => (
                    <div
                      key={t.name}
                      className={cn(
                        'flex flex-col gap-1.5 px-4 py-3.5 sm:flex-row sm:items-start sm:gap-4 sm:px-5',
                        i !== TOOLS.length - 1 && 'border-b',
                      )}
                    >
                      <div className="flex flex-shrink-0 items-center gap-2 sm:w-56">
                        <code className="font-mono text-sm font-semibold text-primary">{t.name}</code>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-muted-foreground">{t.desc}</p>
                      </div>
                      <div className="flex-shrink-0">
                        <AuthPill auth={t.auth} />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  Beyond MCP, the same operations are available over plain REST, plus a{' '}
                  natural-language endpoint at{' '}
                  <code>POST {API_BASE}/ask</code> (NLWeb, supports SSE) if you&apos;d rather hand
                  the agent a sentence than a tool schema.
                </p>
              </Section>

              {/* ---------------------------------------------------------- *
                  FIX LOOP
               * ---------------------------------------------------------- */}
              <Section id="fix-loop" icon={<Sparkles className="h-5 w-5" />} title="The fix loop">
                <p>
                  This is the part other tools don&apos;t have. An agent doesn&apos;t stop at
                  &ldquo;your SPF is broken.&rdquo; It closes the loop to &ldquo;your SPF is
                  fixed.&rdquo; Three steps, server-mediated the whole way:
                </p>

                <FixLoopDiagram />

                <ul className="mt-2 space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                    <span>
                      <strong>Server-revalidated.</strong> <code>apply_dns_fix</code> recomputes the
                      diff and rejects anything that doesn&apos;t match the plan. An agent
                      can&apos;t publish arbitrary records.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                    <span>
                      <strong>Two-step + destructive.</strong> You pass the <code>connectionId</code>{' '}
                      and <code>ops</code> from <code>get_dns_fix_plan</code> verbatim. It needs an
                      owner/admin <code>write</code>/<code>full</code> key.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                    <span>
                      <strong>Registrars:</strong> Cloudflare, Route 53, GoDaddy, and Namecheap.
                      SPF sender lists, DKIM keys, and BIMI logos come back as{' '}
                      <code>manualReview</code> rather than being auto-applied.
                    </span>
                  </li>
                </ul>

                <p className="text-sm">
                  Every scan response also carries a <code>remediations</code> array (the exact
                  record to publish per failing check, with an <code>autoFixable</code> flag) and a{' '}
                  <code>dnssec</code> block (DS/DNSKEY chain + DANE/TLSA), so an agent has the fix
                  in hand the moment it sees the problem. Full walkthrough:{' '}
                  <a
                    href="/skills/fix-dns/SKILL.md"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary hover:underline"
                  >
                    /skills/fix-dns/SKILL.md
                  </a>
                  .
                </p>
              </Section>

              {/* ---------------------------------------------------------- *
                  API + SDKs
               * ---------------------------------------------------------- */}
              <Section id="api" icon={<CodeIcon className="h-5 w-5" />} title="REST API & SDKs">
                <p>
                  Prefer plain HTTP? Everything the agent surface does is available over a typed
                  REST API. URLs are unversioned and stable; the served version is echoed in the{' '}
                  <code>API-Version</code> response header, and you can opt into a future breaking
                  version with the optional <code>Accept-Version</code> request header.
                </p>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <ResourceCard
                    icon={<CodeIcon className="h-4 w-4" />}
                    title="OpenAPI 3.1"
                    body="Every operation typed. Loads straight into function-calling clients."
                    href="/openapi.json"
                    cta="openapi.json"
                  />
                  <ResourceCard
                    icon={<BookOpen className="h-4 w-4" />}
                    title="REST reference"
                    body="Auth, endpoints, errors, rate limits, and versioning."
                    to="/api-docs"
                    cta="API docs"
                  />
                  <ResourceCard
                    icon={<KeyRound className="h-4 w-4" />}
                    title="Authentication"
                    body="Bearer keys, read vs. write/full scopes, the error envelope."
                    href="/auth.md"
                    cta="auth.md"
                  />
                </div>

                <p>
                  Try it without a key. The anonymous scan endpoint is rate-limited to 5/hour per
                  IP and returns the same scored report:
                </p>
                <CodeBlock>
                  {`curl -sS -X POST ${API_BASE}/scan-domain \\
  -H 'content-type: application/json' \\
  -d '{"domain":"example.com"}'`}
                </CodeBlock>

                <div className="rounded-lg border bg-card p-5">
                  <div className="flex items-center gap-2">
                    <Boxes className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold tracking-tight">TypeScript / JavaScript SDK</h3>
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                      live on npm
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    The official thin client wraps the REST surface with typed helpers,
                    retry-on-429, and pagination.
                  </p>
                  <CodeBlock>npm install inboxguard</CodeBlock>
                  <p className="mt-2 text-sm text-muted-foreground">
                    <a className="text-primary hover:underline" href="https://www.npmjs.com/package/inboxguard" target="_blank" rel="noopener noreferrer">npmjs.com/package/inboxguard</a>
                    {' · '}
                    <a className="text-primary hover:underline" href="https://github.com/movaMedia-Inc/inboxguard-js" target="_blank" rel="noopener noreferrer">source on GitHub</a>. A
                    Python SDK is on the way; until then the OpenAPI contract above generates a typed
                    client in any language.
                  </p>
                </div>

                <div className="mt-4 rounded-lg border bg-card p-5">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold tracking-tight">Agent skills (skills.sh)</h3>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Three ready-made skills (scan a domain, stage a DMARC rollout, detect-and-fix
                    DNS), installable into any skills-compatible agent:
                  </p>
                  <CodeBlock>npx skills add movaMedia-Inc/inboxguard-skills</CodeBlock>
                  <p className="mt-2 text-sm text-muted-foreground">
                    <a className="text-primary hover:underline" href="https://github.com/movaMedia-Inc/inboxguard-skills" target="_blank" rel="noopener noreferrer">github.com/movaMedia-Inc/inboxguard-skills</a>
                    {' '}also carries the AGENTS.md / .cursorrules agent configs.
                  </p>
                </div>
              </Section>

              {/* ---------------------------------------------------------- *
                  DISCOVERY SURFACE
               * ---------------------------------------------------------- */}
              <Section id="discovery" icon={<Search className="h-5 w-5" />} title="Discovery surface">
                <p>
                  Machine-readable entry points so an agent can find and learn InboxGuard on its
                  own. All are served as static files at the apex.
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <AgentLink href="/llms.txt" label="/llms.txt" note="index for LLMs" />
                  <AgentLink href="/llms-full.txt" label="/llms-full.txt" note="full manual" />
                  <AgentLink href="/agents.md" label="/agents.md" note="agent how-to" />
                  <AgentLink href="/auth.md" label="/auth.md" note="authentication" />
                  <AgentLink href="/openapi.json" label="/openapi.json" note="API contract" />
                  <AgentLink href="/.well-known/mcp/manifest.json" label="/.well-known/mcp/manifest.json" note="MCP manifest" />
                  <AgentLink href="/.well-known/mcp/server-card.json" label="/.well-known/mcp/server-card.json" note="server card" />
                  <AgentLink href="/.well-known/agent-skills/index.json" label="/.well-known/agent-skills/index.json" note="skills index" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Agent skills (publishable walkthroughs) live under{' '}
                  <a
                    href="/skills/scan-domain/SKILL.md"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-primary hover:underline"
                  >
                    /skills/
                  </a>
                  : <code>scan-domain</code>, <code>dmarc-monitoring</code>, and{' '}
                  <code>fix-dns</code>.
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Listed on</span> the{' '}
                  <a href="https://registry.modelcontextprotocol.io/v0/servers?search=inboxguard" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">official MCP Registry</a>{' '}
                  as <code>io.inboxguard/email-deliverability</code>, plus{' '}
                  <a href="https://smithery.ai" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Smithery</a>{' '}
                  and <a href="https://mcp.so" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">mcp.so</a>. Skills on{' '}
                  <a href="https://skills.sh" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">skills.sh</a>; SDK on{' '}
                  <a href="https://www.npmjs.com/package/inboxguard" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">npm</a>.
                </p>
              </Section>

              {/* Footer CTA */}
              <div className="rounded-lg border bg-card p-6 text-center">
                <h3 className="text-lg font-semibold tracking-tight">
                  Ship an agent that fixes deliverability
                </h3>
                <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                  Create a free account, mint an API key, and connect the MCP server. The free tier
                  covers a single domain forever.
                </p>
                <div className="mt-4 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/auth?mode=sign-up"
                    className="inline-flex h-10 items-center justify-center gap-1 rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    Create free account
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    Talk to us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </main>

        <PublicFooter />
      </div>
    </>
  );
}

/* ================================================================== *
 * Install guides — tabbed, copy-pasteable client configs.
 * ================================================================== */

interface ClientGuide {
  id: string;
  label: string;
  /** Short framing line above the snippet. */
  intro: React.ReactNode;
  /** Optional filename badge shown on the code block (e.g. the config path). */
  file?: string;
  /** The copy-pasteable body. */
  code: string;
  /** Optional steps / notes under the snippet. */
  note?: React.ReactNode;
}

const CLIENT_GUIDES: ClientGuide[] = [
  {
    id: 'claude-desktop',
    label: 'Claude Desktop',
    intro: (
      <>
        Add InboxGuard to the <code>mcpServers</code> block of your{' '}
        <code>claude_desktop_config.json</code> (Settings → Developer → Edit Config). Claude
        Desktop speaks Streamable HTTP directly:
      </>
    ),
    file: 'claude_desktop_config.json',
    code: `{
  "mcpServers": {
    "inboxguard": {
      "type": "streamable-http",
      "url": "${MCP_URL}",
      "headers": {
        "Authorization": "Bearer ig_live_…"
      }
    }
  }
}`,
    note: (
      <>
        Restart Claude Desktop after saving. The InboxGuard tools then appear in the tools menu
        (the slider icon). On an older build without native HTTP transport, bridge it with{' '}
        <code>npx mcp-remote {MCP_URL} --header &quot;Authorization: Bearer ig_live_…&quot;</code>{' '}
        as the command instead.
      </>
    ),
  },
  {
    id: 'chatgpt',
    label: 'ChatGPT',
    intro: (
      <>
        In ChatGPT, enable <strong>Developer mode</strong> (Settings → Connectors → Advanced) and
        add a custom connector pointing at the MCP URL:
      </>
    ),
    code: `MCP server URL:  ${MCP_URL}
Transport:       Streamable HTTP
Authentication:  Bearer token  →  ig_live_…`,
    note: (
      <>
        Paste the URL into <em>Settings → Connectors → Create / Add custom connector</em>, choose
        bearer-token auth, and paste your key. Discovery is open, so ChatGPT can list the tools as
        soon as the connector is added; calls that read or change your account data use the key.
      </>
    ),
  },
  {
    id: 'cursor',
    label: 'Cursor',
    intro: (
      <>
        Add a project-level <code>.cursor/mcp.json</code> (or the global one in your home dir).
        Cursor connects to the server over Streamable HTTP:
      </>
    ),
    file: '.cursor/mcp.json',
    code: `{
  "mcpServers": {
    "inboxguard": {
      "url": "${MCP_URL}",
      "headers": {
        "Authorization": "Bearer ig_live_…"
      }
    }
  }
}`,
    note: (
      <>
        Open <em>Settings → MCP</em> in Cursor to confirm the server is connected and the 28 tools
        are listed. Commit a redacted version (or keep the key in an env var) so you don&apos;t
        check a live secret into the repo.
      </>
    ),
  },
  {
    id: 'claude-code',
    label: 'Claude Code',
    intro: <>One command. Claude Code adds the remote server and stores the auth header for you:</>,
    file: 'terminal',
    code: `claude mcp add --transport http inboxguard ${MCP_URL} \\
  --header "Authorization: Bearer ig_live_…"`,
    note: (
      <>
        Verify with <code>claude mcp list</code>. Use <code>--scope project</code> to share the
        server via a checked-in <code>.mcp.json</code>, or <code>--scope user</code> to make it
        available across all your projects.
      </>
    ),
  },
  {
    id: 'any',
    label: 'Any MCP client',
    intro: (
      <>
        Any client that speaks the MCP Streamable HTTP transport works the same way. Give it the
        URL and a bearer header:
      </>
    ),
    code: `URL:            ${MCP_URL}
Transport:      Streamable HTTP
Auth header:    Authorization: Bearer ig_live_…
Discovery:      open (initialize / tools/list need no auth)
tools/call:     requires the bearer key`,
    note: (
      <>
        No native HTTP transport? Bridge stdio → HTTP with{' '}
        <code>npx mcp-remote {MCP_URL} --header &quot;Authorization: Bearer ig_live_…&quot;</code>.
        The manifest at{' '}
        <a
          href="/.well-known/mcp/manifest.json"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-primary hover:underline"
        >
          /.well-known/mcp/manifest.json
        </a>{' '}
        describes the server programmatically.
      </>
    ),
  },
];

function InstallGuides() {
  const [active, setActive] = useState(CLIENT_GUIDES[0].id);
  const guide = CLIENT_GUIDES.find((g) => g.id === active) ?? CLIENT_GUIDES[0];

  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      {/* Tab strip — horizontally scrollable on narrow screens. */}
      <div
        role="tablist"
        aria-label="MCP client install guides"
        className="flex gap-1 overflow-x-auto border-b bg-muted/40 p-1.5"
      >
        {CLIENT_GUIDES.map((g) => {
          const selected = g.id === active;
          return (
            <button
              key={g.id}
              role="tab"
              aria-selected={selected}
              onClick={() => setActive(g.id)}
              className={cn(
                'whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                selected
                  ? 'bg-background text-foreground shadow-sm ring-1 ring-border'
                  : 'text-muted-foreground hover:bg-background/60 hover:text-foreground',
              )}
            >
              {g.label}
            </button>
          );
        })}
      </div>

      <div className="p-4 sm:p-5">
        <p className="text-sm leading-relaxed text-foreground [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]">
          {guide.intro}
        </p>
        <div className="mt-3">
          <CodeBlock file={guide.file} copyValue={guide.code}>
            {guide.code}
          </CodeBlock>
        </div>
        {guide.note && (
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.95em] [&_code]:break-all">
            {guide.note}
          </p>
        )}
      </div>
    </div>
  );
}

/* ================================================================== *
 * Fix-loop diagram — three steps, responsive (row on desktop, stacked
 * with down-arrows on mobile). Pure presentational.
 * ================================================================== */

function FixLoopDiagram() {
  const steps = [
    {
      icon: <Search className="h-5 w-5" />,
      tool: 'scan_domain',
      title: 'Detect',
      body: 'Score the domain. Each failing check comes back with the exact record to publish.',
    },
    {
      icon: <ListChecks className="h-5 w-5" />,
      tool: 'get_dns_fix_plan',
      title: 'Plan',
      body: 'Preview the diff against live DNS: what changes, at which connected registrar.',
    },
    {
      icon: <ShieldCheck className="h-5 w-5" />,
      tool: 'apply_dns_fix',
      title: 'Apply + re-scan',
      body: 'Publish the records, server-revalidated, then scan again to confirm the fix landed.',
    },
  ];

  return (
    <div className="grid items-stretch gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr]">
      {steps.map((s, i) => (
        <div key={s.tool} className="contents">
          <div className="flex flex-col rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent text-primary">
                {s.icon}
              </span>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Step {i + 1}
                </div>
                <div className="text-sm font-semibold tracking-tight">{s.title}</div>
              </div>
            </div>
            <code className="mt-3 inline-block w-fit rounded bg-muted px-2 py-0.5 font-mono text-xs font-medium text-primary">
              {s.tool}
            </code>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{s.body}</p>
          </div>
          {i < steps.length - 1 && (
            <div className="flex items-center justify-center text-muted-foreground/60">
              <ArrowRight className="hidden h-5 w-5 sm:block" />
              <ArrowDown className="h-5 w-5 sm:hidden" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ================================================================== *
 * Shared building blocks (Section / CopyField / CodeBlock / Callout /
 * AgentLink / AuthPill / ResourceCard). Visual language matches
 * ApiDocsPage so the two pages feel like one system.
 * ================================================================== */

function Section({
  id,
  icon,
  title,
  children,
}: {
  id: string;
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="flex items-center gap-3 border-b pb-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-accent text-accent-foreground">
          {icon}
        </div>
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      </div>
      <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground [&>p_code]:rounded [&>p_code]:bg-muted [&>p_code]:px-1 [&>p_code]:py-0.5 [&>p_code]:font-mono [&>p_code]:text-[0.85em] [&>ul_code]:rounded [&>ul_code]:bg-muted [&>ul_code]:px-1 [&>ul_code]:py-0.5 [&>ul_code]:font-mono [&>ul_code]:text-[0.85em]">
        {children}
      </div>
    </section>
  );
}

function Callout({
  tone,
  icon,
  title,
  children,
}: {
  tone: 'warn' | 'info';
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  const cls =
    tone === 'warn'
      ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/30 dark:text-amber-200'
      : 'border-primary/30 bg-primary/5';
  return (
    <div
      className={cn(
        'flex gap-3 rounded-md border p-3 text-sm [&_code]:rounded [&_code]:bg-muted/70 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]',
        cls,
      )}
    >
      <div className="mt-0.5 flex-shrink-0">{icon}</div>
      <div>
        <p className="font-medium">{title}</p>
        <p className="mt-1">{children}</p>
      </div>
    </div>
  );
}

function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — fail quietly.
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={copied ? 'Copied' : label}
      className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent"
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5 text-success" />
          Copied
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" />
          {label}
        </>
      )}
    </button>
  );
}

/** Labelled value with a copy button — for the MCP URL / auth header. */
function CopyField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="mt-2 flex items-center justify-between gap-3">
        <code
          className={cn(
            'min-w-0 break-all text-sm text-foreground',
            mono && 'font-mono',
          )}
        >
          {value}
        </code>
        <CopyButton value={value} label="Copy" />
      </div>
    </div>
  );
}

function CodeBlock({
  children,
  file,
  copyValue,
}: {
  children: React.ReactNode;
  file?: string;
  copyValue?: string;
}) {
  return (
    <div className="overflow-hidden rounded-md border shadow-sm">
      {(file || copyValue) && (
        <div className="flex items-center justify-between gap-2 border-b border-slate-700/60 bg-slate-800 px-3 py-1.5 dark:bg-slate-900">
          {file ? (
            <span className="font-mono text-[11px] tracking-wide text-slate-400">{file}</span>
          ) : (
            <span />
          )}
          {copyValue && <CopyButton value={copyValue} label="Copy" />}
        </div>
      )}
      <pre className="overflow-x-auto bg-slate-900 px-4 py-3 font-mono text-xs leading-relaxed text-slate-100 dark:bg-slate-800">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function AgentLink({ href, label, note }: { href: string; label: string; note?: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex min-w-0 items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 transition-colors hover:border-primary/40"
    >
      <span className="inline-flex min-w-0 items-center gap-1.5">
        <span className="truncate font-mono text-sm text-primary group-hover:underline">{label}</span>
        <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-50" />
      </span>
      {note && <span className="flex-shrink-0 text-xs text-muted-foreground">{note}</span>}
    </a>
  );
}

function AuthPill({ auth }: { auth: 'open' | 'read' | 'write' }) {
  // Escalating auth requirement, graded emerald → slate → amber so the three
  // levels stay distinct (open and read would otherwise both read as emerald).
  const map = {
    open: { label: 'open', cls: 'bg-success/10 text-success' },
    read: { label: 'read key', cls: 'bg-secondary text-foreground' },
    write: { label: 'write key', cls: 'bg-warning/15 text-warning' },
  } as const;
  const { label, cls } = map[auth];
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium', cls)}>
      {label}
    </span>
  );
}

function ResourceCard({
  icon,
  title,
  body,
  href,
  to,
  cta,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  href?: string;
  to?: string;
  cta: string;
}) {
  const inner = (
    <>
      <div className="flex items-center gap-2">
        <span className="text-primary">{icon}</span>
        <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
      </div>
      <p className="mt-1.5 flex-1 text-xs leading-relaxed text-muted-foreground">{body}</p>
      <span className="mt-3 inline-flex items-center gap-1 font-mono text-xs font-medium text-primary">
        {cta}
        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
      </span>
    </>
  );
  const cls =
    'card-elev group flex flex-col rounded-lg border bg-card p-4 transition-colors hover:border-primary/40';
  if (to) {
    return (
      <Link to={to} className={cls}>
        {inner}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
      {inner}
    </a>
  );
}

export default DevelopersPage;
